import { getRequestAuth } from './auth/requestAuth';
import type {
  AuthConfigOut,
  BudgetRequestDecisionIn,
  BudgetRequestIn,
  BudgetRequestListOut,
  BudgetRequestOut,
  ClaimKeyIn,
  ClaudeCodeOs,
  ClaudeCodeSetupOut,
  CustomerFlagIn,
  DriftListOut,
  GuardrailListOut,
  GuardrailRequestDecisionIn,
  GuardrailRequestIn,
  GuardrailRequestListOut,
  GuardrailRequestOut,
  HealthOut,
  Identity,
  JobStatusListOut,
  KeyCreatedOut,
  KeyCreateIn,
  KeyGuardrailChangeIn,
  KeyGuardrailNameListOut,
  KeyGuardrailOut,
  KeyListOut,
  KeyMutationOut,
  KeyOut,
  KeySpendOut,
  KeyUpdateIn,
  MyModelsOut,
  TeamBudgetIn,
  TeamConfigListOut,
  TeamConfigMutationOut,
  TeamSpendOut,
  TeamSummary,
  UnmanagedKeyListOut,
  UnmanagedKeyOut,
} from './types';

/**
 * Relative base: in production FastAPI serves the SPA and the API from the same
 * origin, and in dev Vite proxies /api to localhost:8000. Never hardcode an origin.
 */
const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Same-origin absolute URL. Explicit because Node's fetch (used under vitest)
 * rejects bare relative paths, unlike the browser's.
 */
function url(path: string): string {
  const base = typeof location === 'undefined' ? 'http://localhost' : location.href;
  return new URL(`${API_BASE}${path}`, base).toString();
}

/**
 * Bearer token, acquired per request rather than captured once at mount. MSAL serves it
 * from its own cache and renews silently near expiry, so the repeat call is cheap and a
 * tab left open overnight does not start 401ing.
 *
 * Absent in mock mode by design: nothing registered means no header, never a
 * placeholder. A failed acquisition is not fatal here — the request goes out bare and
 * the API's own 401 drives recovery, so one MSAL hiccup cannot blank the page.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const auth = getRequestAuth();
  if (!auth) return {};
  try {
    return { Authorization: `Bearer ${await auth.token()}` };
  } catch {
    return {};
  }
}

/**
 * Shared logic for all authenticated requests: auth header, 401 recovery, error parsing.
 * No AbortSignal parameter on purpose: jsdom installs its own AbortController, which
 * Node's undici fetch refuses with a TypeError, so passing one makes every request
 * fail under test while working in the browser. Callers discard stale responses
 * instead (see App.tsx) — these are read-only GETs, so nothing needs hard aborting.
 *
 * `authorised` says whether the API actually checks a token on this path. It is false
 * for /auth-config and /health, which are unauthenticated server-side (the ALB polls
 * health), so their 200 is not evidence our session works and must not re-arm the
 * re-auth guard — otherwise the guard resets on every load and the loop it exists to
 * prevent comes back.
 */
async function request<T>(
  method: string,
  path: string,
  body: unknown = undefined,
  authorised = true,
): Promise<T> {
  let res: Response;
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(await authHeaders()),
    };
    const init: RequestInit = {
      method,
      headers,
      credentials: 'same-origin',
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    res = await fetch(url(path), init);
  } catch {
    throw new ApiError(0, `Cannot reach the API (${path}). Is the backend running?`);
  }

  const auth = getRequestAuth();
  if (authorised && auth) {
    if (res.status === 401) {
      // MSAL thought the token was good and the API disagreed, so its cache is wrong
      // about our session. The guard decides whether a fresh sign-in could even help.
      if (await auth.recover()) {
        throw new ApiError(401, 'Your session expired. Signing you in again...', 'reauth');
      }
    } else if (res.ok) {
      auth.accepted();
    }
  }

  if (!res.ok) {
    // FastAPI errors are ErrorOut, but a proxy or crash can return HTML instead.
    let detail = `${res.status} ${res.statusText}`;
    let code: string | null = null;
    try {
      const body = (await res.json()) as Partial<{ detail: unknown; code: unknown }>;
      if (typeof body.detail === 'string' && body.detail) detail = body.detail;
      if (typeof body.code === 'string') code = body.code;
    } catch {
      /* non-JSON body: keep the status line */
    }
    throw new ApiError(res.status, detail, code);
  }

  return (await res.json()) as T;
}

async function get<T>(path: string, authorised = true): Promise<T> {
  return request<T>('GET', path, undefined, authorised);
}

/**
 * Fetch a file download (CSV, PDF, etc.) and trigger a browser save-as. The response
 * is not JSON. Returns the filename from the Content-Disposition header if present.
 */
async function download(path: string, defaultFilename: string): Promise<string> {
  const headers = await authHeaders();
  const res = await fetch(url(path), {
    method: 'GET',
    headers: {
      ...headers,
      Accept: 'text/csv,application/octet-stream',
    },
    credentials: 'same-origin',
  });

  if (!res.ok) {
    throw new ApiError(res.status, `Download failed: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  // Extract filename from Content-Disposition if present, else use default
  const disposition = res.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (disposition) {
    const match = /filename[^;=\n]*=(['"]?)([^'"\n]*)(\1)/.exec(disposition);
    if (match?.[2]) filename = match[2];
  }
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
  return filename;
}

export const api = {
  // Both unauthenticated server-side, so a 200 here says nothing about our session.
  authConfig: () => get<AuthConfigOut>('/auth-config', false),
  health: () => get<HealthOut>('/health', false),
  me: () => get<Identity>('/me'),
  teams: () => get<TeamSummary[]>('/teams'),
  // `refresh` reads OpenRouter directly instead of our cached rows. Off for the initial
  // load so opening the page cannot fan out upstream calls, on when the user asks — which
  // is what the provenance badge tells them Refresh does.
  keys: (refresh = false) => get<KeyListOut>(refresh ? '/keys?refresh=true' : '/keys'),
  createKey: (data: KeyCreateIn) => request<KeyCreatedOut>('POST', '/keys', data),
  updateKey: (id: string, data: KeyUpdateIn) => request<KeyMutationOut>('PATCH', `/keys/${id}`, data),
  setCustomerKey: (id: string, data: CustomerFlagIn) => request<KeyMutationOut>('PATCH', `/keys/${id}/customer`, data),
  disableKey: (id: string) => request<KeyMutationOut>('DELETE', `/keys/${id}`),
  // Budget request endpoints. Role-scoped server-side: admin sees all, lead sees their team's, member sees own.
  budgetRequests: (status?: string) => {
    const path = status ? `/budget-requests?status=${encodeURIComponent(status)}` : '/budget-requests';
    return get<BudgetRequestListOut>(path);
  },
  createBudgetRequest: (data: BudgetRequestIn) => request<BudgetRequestOut>('POST', '/budget-requests', data),
  approveBudgetRequest: (id: string, data: BudgetRequestDecisionIn = {}) =>
    request<BudgetRequestOut>('POST', `/budget-requests/${id}/approve`, data),
  rejectBudgetRequest: (id: string, data: BudgetRequestDecisionIn = {}) =>
    request<BudgetRequestOut>('POST', `/budget-requests/${id}/reject`, data),
  // Guardrail endpoints. Same pattern as budget requests.
  guardrails: (workspaceId: string) => get<GuardrailListOut>(`/guardrails?workspace_id=${encodeURIComponent(workspaceId)}`),
  keyGuardrail: (keyId: string) => get<KeyGuardrailOut>(`/keys/${keyId}/guardrail`),
  keyGuardrailNames: () => get<KeyGuardrailNameListOut>('/keys/guardrail-names'),
  changeKeyGuardrail: (keyId: string, data: KeyGuardrailChangeIn) =>
    request<GuardrailRequestOut>('PUT', `/keys/${keyId}/guardrail`, data),
  myModels: () => get<MyModelsOut>('/me/models'),
  claudeCodeSetup: (keyId: string, os: ClaudeCodeOs) =>
    get<ClaudeCodeSetupOut>(`/keys/${keyId}/claude-code-setup?os=${os}`),
  guardrailRequests: (status?: string) => {
    const path = status ? `/guardrail-requests?status=${encodeURIComponent(status)}` : '/guardrail-requests';
    return get<GuardrailRequestListOut>(path);
  },
  createGuardrailRequest: (data: GuardrailRequestIn) => request<GuardrailRequestOut>('POST', '/guardrail-requests', data),
  approveGuardrailRequest: (id: string, data: GuardrailRequestDecisionIn = {}) =>
    request<GuardrailRequestOut>('POST', `/guardrail-requests/${id}/approve`, data),
  rejectGuardrailRequest: (id: string, data: GuardrailRequestDecisionIn = {}) =>
    request<GuardrailRequestOut>('POST', `/guardrail-requests/${id}/reject`, data),
  // Admin endpoints. All return 404 for non-admins (by design) and must not be fatal.
  unmanagedKeys: (status?: string) => {
    const path = status ? `/admin/unmanaged-keys?status=${encodeURIComponent(status)}` : '/admin/unmanaged-keys';
    return get<UnmanagedKeyListOut>(path);
  },
  claimUnmanagedKey: (id: string, data: ClaimKeyIn) =>
    request<KeyOut>('POST', `/admin/unmanaged-keys/${id}/claim`, data),
  ignoreUnmanagedKey: (id: string) =>
    request<UnmanagedKeyOut>('POST', `/admin/unmanaged-keys/${id}/ignore`),
  drift: () => get<DriftListOut>('/admin/drift'),
  jobStatus: () => get<JobStatusListOut>('/admin/jobs'),
  teamConfig: () => get<TeamConfigListOut>('/admin/teams'),
  setTeamBudget: (teamId: string, data: TeamBudgetIn) =>
    request<TeamConfigMutationOut>('PATCH', `/admin/teams/${teamId}`, data),
  // M6 spend time-series endpoints
  keySpend: (keyId: string, days = 30) =>
    get<KeySpendOut>(`/keys/${encodeURIComponent(keyId)}/spend?days=${days}`),
  teamSpend: (teamId: string, days = 30) =>
    get<TeamSpendOut>(`/teams/${encodeURIComponent(teamId)}/spend?days=${days}`),
  /** Downloads chargeback CSV for the given month (YYYY-MM, defaults to current month). Admin/lead only. */
  downloadChargebackCsv: (month?: string) => {
    const monthParam = month ?? new Date().toISOString().slice(0, 7);
    return download(`/reports/chargeback.csv?month=${encodeURIComponent(monthParam)}`, `chargeback-${monthParam}.csv`);
  },
};
